// src/oura/onboarding/answerText.ts
//
// How the client's form answers read on the staff Form answers drawer. The
// words are the client's own; only the form's leftovers are tidied: the
// "1 - " a Google Form option carries, and the **bold** a pasted answer
// brings. No React, so each rule can be read and checked on its own.

import type { FormAnswers } from "@service/aeo/onboarding";

/** "1 - Staycations (Family/Friends)" -> "Staycations (Family/Friends)". Also "1. " and "1) ". A space must follow, so "7-Eleven" stays whole. */
export function cleanOption(s: string): string {
    return s.replace(/^\s*\d+\s*[-–.)]\s+/, "").trim();
}

/** One piece of a line: plain, or bold where the client wrote **it**. */
export interface TextPart {
    text: string;
    bold: boolean;
}

/** "**How many fit?** Most ask." -> a bold part and a plain part. Stray ** are dropped, never shown. */
export function boldParts(line: string): TextPart[] {
    const parts: TextPart[] = [];
    const re = /\*\*(.+?)\*\*/g;
    let at = 0;
    for (let m = re.exec(line); m; m = re.exec(line)) {
        if (m.index > at) parts.push({ text: line.slice(at, m.index), bold: false });
        parts.push({ text: m[1], bold: true });
        at = m.index + m[0].length;
    }
    if (at < line.length) parts.push({ text: line.slice(at), bold: false });
    return parts.map((p) => ({ ...p, text: p.text.replace(/\*\*/g, "") })).filter((p) => p.text.trim() !== "");
}

/** What customers ask, as the drawer lays it out. */
export type AskBlock =
    | { kind: "ask"; question: string; answer: string }
    | { kind: "text"; text: string };

/**
 * The client's "what customers ask" answer, one block per line. A line that
 * opens with a **bold question** becomes a numbered question with the rest as
 * its answer; any other line (an intro, a "close behind") stays as written.
 */
export function askBlocks(items: string[]): AskBlock[] {
    const lines = items.flatMap((s) => s.split(/\r?\n/)).map((s) => s.trim()).filter(Boolean);
    return lines.map((raw): AskBlock => {
        const line = raw.replace(/^[-*•]\s+(?=\S)/, "").replace(/^\d+[.)]\s+/, "");
        const m = /^\*\*(.+?)\*\*[\s:–-]*(.*)$/.exec(line);
        if (m) return { kind: "ask", question: m[1].trim(), answer: m[2].trim() };
        return { kind: "text", text: line };
    });
}

/** The answers the client left empty, by the label the drawer shows. Plans and Anything else are optional. */
export function missingAnswers(a: FormAnswers): string[] {
    const missing: string[] = [];
    if (a.services.length === 0) missing.push("Services");
    if (a.outletAreas.length === 0) missing.push("Outlet areas");
    if (a.customerQuestions.length === 0) missing.push("What customers ask");
    if (a.goals.length === 0) missing.push("Goals");
    if (!a.approver.trim()) missing.push("Approves changes");
    if (!a.accessContact) missing.push("Looks after access");
    return missing;
}

/** The empty answers the first AI check cannot run without. */
export function missingForCheck(a: FormAnswers): string[] {
    return missingAnswers(a).filter((m) => m === "Services" || m === "Outlet areas");
}

/** ["Outlet areas"] -> "Outlet areas"; two -> "A and B"; more -> "A, B and C". */
export function andList(items: string[]): string {
    if (items.length <= 1) return items.join("");
    return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/** One part of a Knowledge note: a "Goals:" style heading over its lines, or loose lines with no heading. */
export interface NoteSection {
    label: string | null;
    /** Bullet lines, without their "- ". */
    items: string[];
    /** Lines that were not bullets, as written. */
    text: string;
}

/** The note's own headings, as the Form answers drawer words them. */
const NOTE_LABELS: Record<string, string> = { "upcoming plans": "Plans" };

/**
 * A pasted Knowledge note split into its parts, so the form's notes ("Goals:\n- …\n\nUpcoming plans:\n…")
 * read like the Form answers drawer. A part opens with a heading when its first line is short and ends in ":".
 */
export function noteSections(text: string): NoteSection[] {
    return text
        .split(/\r?\n\s*\r?\n/)
        .map((block) => block.split(/\r?\n/).map((l) => l.trimEnd()).filter((l) => l.trim()))
        .filter((lines) => lines.length > 0)
        .map((lines): NoteSection => {
            const head = /^([^:]{1,40}):$/.exec(lines[0].trim());
            const body = head ? lines.slice(1) : lines;
            const label = head ? NOTE_LABELS[head[1].trim().toLowerCase()] ?? head[1].trim() : null;
            const bullets = body.filter((l) => /^\s*[-*•]\s+/.test(l));
            const items = bullets.length === body.length ? body.map((l) => l.replace(/^\s*[-*•]\s+/, "").trim()) : [];
            return { label, items, text: items.length ? "" : body.map((l) => l.trim()).join("\n") };
        });
}
